import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs/Observable';
import { ToastrService } from 'ngx-toastr';
import {
  Tramite,
  Microproceso,
  HistorialMicroproceso,
  ConsultaTramite,
  Filtro,
  EstructuraEspecifica,
  ValoresEspecificos,
  ModeloBase
} from '@app/shared';
import { Logger, EndpointService } from '@app/core';
import { Const } from '@app/shared';

const log = new Logger('FlowService');

export interface UserPersistent {
  id?: number;
  alias?: string;
  user_id?: number;
  identificacion?: string;
  nombre?: string;
  apellido?: string;
}

@Injectable()
export class FlowService {
  PATH_SERVICES = Const.ROUTES_SYSTEM_INTEGRATIONS.cinco.services;
  constructor(private http: HttpClient, private toastr: ToastrService, private api: EndpointService) {}

  /**
   * Retorna los tramites por default con sus actividades.
   *
   * @param {number} idDefaultTramite
   * @returns {Observable<ConsultaTramite[]>}
   * @memberof FlowService
   */
  getInstanceDefaultTramites(idDefaultTramite: number, filters?: any): Observable<ConsultaTramite[]> {
    return new Observable(obs => {
      const dataForm = {
        id: idDefaultTramite
      };
      if (filters) {
        dataForm['filtros'] = filters;
      }
      this.http.post<ConsultaTramite[]>(this.api.get('instanceDefaultTramite'), dataForm).subscribe(
        instances => {
          obs.next(instances);
          obs.complete();
        },
        err => {
          obs.error(err);
        }
      );
    });
  }

  /**
   * Retorna una instancia de un microproceso.
   *
   * @param {number} idTarea
   * @returns {Observable<Microproceso>}
   * @memberof FlowService
   */
  getMicroProcess(idTarea: number): Observable<Microproceso> {
    return new Observable(obs => {
      const path = this.PATH_SERVICES + Const.ROUTES_SYSTEM_INTEGRATIONS.cinco.endpoints.workflowMicroproces;
      this.http.get<Microproceso>(this.api.addParams(path, 'simple', [idTarea])).subscribe(
        instance => {
          obs.next(instance);
          obs.complete();
        },
        err => {
          obs.error(err);
        }
      );
    });
  }

  /**
   * Retorna una instancia de un tramite por default.
   *
   * @param {number} idTramite
   * @returns {Observable<Tramite>}
   * @memberof FlowService
   */
  getInstance(idTramite: number): Observable<Tramite> {
    return new Observable(obs => {
      const path = this.PATH_SERVICES + Const.ROUTES_SYSTEM_INTEGRATIONS.cinco.endpoints.workflow;
      this.http.get<Tramite>(this.api.addParams(path, 'simple', [idTramite])).subscribe(
        instance => {
          obs.next(instance);
          obs.complete();
        },
        err => {
          obs.error(err);
        }
      );
    });
  }

  /**
   * Consulta el historial de versiones de un microproceso
   *
   * @param {number} idTask
   * @returns {Promise<HistorialMicroproceso[]>}
   * @memberof FlowService
   */
  getHistoryMicroproces(idTask: number): Promise<HistorialMicroproceso[]> {
    return new Promise((resolve, reject) => {
      const path = this.PATH_SERVICES + Const.ROUTES_SYSTEM_INTEGRATIONS.cinco.endpoints.workflowMicroprocesHistory;
      this.http.get<HistorialMicroproceso[]>(this.api.addParams(path, 'simple', [idTask])).subscribe(
          historyMicroproces => {
            resolve(historyMicroproces);
          },
          err => {
            reject(err);
          }
        );
    });
  }

  /**
   * Consulta los filtros para la consulta de el tramite
   *
   * @param {number} idTramite
   * @returns {Promise<Filtro>}
   * @memberof FlowService
   */
  getFiltersShearchTramite(idTramite: number): Promise<Filtro> {
    return new Promise((resolve, reject) => {
      this.http.get<Filtro>(this.api.get('filterTramiteParameter', [idTramite])).subscribe(
          filter => {
            resolve(filter);
          },
          err => {
            reject(err);
          }
        );
    });
  }

  /**
   * Consulta una maestra sus columnas en especifico
   *
   * @param {EstructuraEspecifica} structure
   * @returns {Promise<ValoresEspecificos[]>}
   * @memberof FlowService
   */
  getColumnsSpecificMasterTable(
    structure: EstructuraEspecifica,
  ): Promise<ValoresEspecificos[]> {
    return new Promise((resolve, reject) => {
      const formData = {
        estructura: structure
      };
      this.http.post<ValoresEspecificos[]>(this.api.get('masterTableSpecificInformation'), formData).subscribe(
          specificValues => {
            resolve(specificValues);
          },
          err => {
            reject(err);
          }
        );
    });
  }

   /**
   * Consulta los usuarios persistentes por trámite
   *
   * @memberof FlowService
   */
  getPersistentUsers(
    idTramite: number
  ): Promise<UserPersistent[]> {
    const path = this.PATH_SERVICES + Const.ROUTES_SYSTEM_INTEGRATIONS.cinco.endpoints.persistenUsers;
    return new Promise((resolve, reject) => {
      this.http.get<UserPersistent[]>(this.api.addParams(path, 'simple', [idTramite])).subscribe(
        data => {
          resolve(data);
        },
        err => {
          reject(err);
        }
      );
    });
  }

  /**
   * Consulta los usuarios del sistema.
   *
   * @param 
   * @returns {Observable<>}
   * @memberof CalendarService
   */
  getSystemUsers(): Promise<any> {
    const path = this.PATH_SERVICES + Const.ROUTES_SYSTEM_INTEGRATIONS.cinco.endpoints.systemUsers;
    return new Promise((resolve, reject) => {
      this.http.get(this.api.addParams(path, 'simple', [])).subscribe(
        data => {
          resolve(data);
        },
        err => {
          reject(err);
        }
      );
    });
  }

  /**
   * actualiza los usuarios persistentes de la tarea
   *
   * @param 
   * @returns {Observable<>}
   * @memberof CalendarService
   */
  taskUpdate(tramiteId?: number, responsibles?: any[], alias?: string, action?: string){
    const path = this.PATH_SERVICES + Const.ROUTES_SYSTEM_INTEGRATIONS.cinco.endpoints.adminAsignation;
    let dataForm = {};
    let message = '';
    if(action == ''){
      if(responsibles.length>1){
        message = 'Usuarios asociados al alias ' + alias + ' exitosamente';
      }else{
        message = 'Usuario asociado al alias ' + alias + ' exitosamente';
      }
      dataForm = {
        id_tramite: tramiteId, 
        id_usuario: responsibles,
        alias: alias,
        accion: action
      }
    }else if(action == 'reemplazar'){
      if(responsibles.length>1){
        message = 'Usuarios asociados al alias ' + alias + ' reemplazados exitosamente';
      }else{
        message = 'Usuario asociado al alias ' + alias + ' reemplazado exitosamente';
      }
      dataForm = {
        id_tramite: tramiteId, 
        id_usuario: responsibles,
        alias: alias,
        accion: action
      }
    }
    return new Promise((resolve, reject) => {
      this.http.post<any>(this.api.addParams(path, 'simple', []), dataForm).subscribe(
        data => {
          this.toastr.success(message, 'Operación exitosa')
          resolve(data);
        },
        err => {
          if(err.status !== 400){
            this.toastr.error('Ha ocurrido un error al realizar la transacción', 'Error en la transacción')
          }else{
            this.toastr.warning('Ha ocurrido un error al realizar la transacción', 'Error en la transacción')
          }
          reject(err);
        }
      );
    });
  }
} // End class



// WEBPACK FOOTER //
// ./src/app/administration/utilitarios-management/shared/flow.service.ts